import {
  setDoc as setDocFirestore,
  updateDoc as updateDocFirestore,
  deleteDoc as deleteDocFirestore,
  DocumentReference, SetOptions, UpdateData, WithFieldValue, PartialWithFieldValue, WriteBatch,
} from 'firebase/firestore';

/*
 * Escrituras que no esperan al servidor.
 *
 * Firestore guarda primero en la caché del dispositivo (ver firebase.ts) y la
 * pantalla ya se actualiza, pero la promesa de setDoc/updateDoc/deleteDoc no
 * se resuelve hasta que el servidor lo confirma. Sin conexión eso no pasa
 * nunca, y los formularios se quedaban en «Guardando…» para siempre.
 *
 * Estas versiones dan la escritura por hecha en cuanto está en el dispositivo:
 * sin red, enseguida; con red lenta, tras unos segundos. Firestore la sube sola
 * al volver la conexión. Si el servidor la rechaza a tiempo (por ejemplo, las
 * reglas), el error llega al formulario como siempre; si la rechaza más tarde,
 * Firestore deshace el cambio en pantalla y se avisa con EVENTO_ESCRITURA_FALLIDA.
 */

/** Sin conexión: margen para que la escritura llegue a la caché local. */
const ESPERA_SIN_RED_MS = 150;
/** Con conexión: lo que se espera al servidor antes de darla por encolada. */
const ESPERA_CON_RED_MS = 4000;

export const EVENTO_ESCRITURA_FALLIDA = 'myarinn:escritura-fallida';

export const sinConexion = () => typeof navigator !== 'undefined' && navigator.onLine === false;

function enDispositivo(escritura: Promise<void>): Promise<void> {
  let resuelta = false;
  const confirmada = escritura.then(() => { resuelta = true; });
  const espera = new Promise<void>(ok =>
    setTimeout(ok, sinConexion() ? ESPERA_SIN_RED_MS : ESPERA_CON_RED_MS));
  return Promise.race([confirmada, espera]).then(() => {
    if (resuelta) return;
    // Se queda en la cola de Firestore: si al final falla, nadie la espera ya
    confirmada.catch(error => {
      console.warn('Una escritura pendiente no se pudo guardar', error);
      window.dispatchEvent(new CustomEvent(EVENTO_ESCRITURA_FALLIDA, { detail: error }));
    });
  });
}

export function setDoc<A, D extends Record<string, any>>(
  ref: DocumentReference<A, D>, data: WithFieldValue<A>): Promise<void>;
export function setDoc<A, D extends Record<string, any>>(
  ref: DocumentReference<A, D>, data: PartialWithFieldValue<A>, options: SetOptions): Promise<void>;
export function setDoc(ref: DocumentReference<any, any>, data: any, options?: SetOptions): Promise<void> {
  return enDispositivo(options ? setDocFirestore(ref, data, options) : setDocFirestore(ref, data));
}

export function updateDoc<A, D extends Record<string, any>>(
  ref: DocumentReference<A, D>, data: UpdateData<D>): Promise<void> {
  return enDispositivo(updateDocFirestore(ref, data));
}

export function deleteDoc(ref: DocumentReference<any, any>): Promise<void> {
  return enDispositivo(deleteDocFirestore(ref));
}

/** batch.commit() con el mismo trato que las escrituras sueltas. */
export function confirmarLote(batch: WriteBatch): Promise<void> {
  return enDispositivo(batch.commit());
}
